import React, {useCallback, useEffect, useMemo, useState} from "react";
import {useLoaderData, useParams, useSearchParams} from "react-router";
import {format, isValid, parse} from "date-fns";
import {
    FaArrowDown,
    FaExclamationTriangle,
    FaExternalLinkAlt,
    FaInfoCircle,
    FaPlus,
    FaPrint
} from "react-icons/fa";
import {keepPreviousData, useQuery} from "@tanstack/react-query";
import {Template} from "../../../../components/template/template.jsx";
import Button from "../../../../components/button/button.jsx";
import Dropzone from "../../../../components/dropzone/dropzone.jsx";
import RegisteredLocation from "../../../../components/registered-location/registered-location.jsx";
import {navigateHook} from "../../../../hooks/use-navigate.js";
import useHasPermission from "../../../../hooks/use-has-permission.js";
import {PermissionEnum} from "../../../../utils/enums/permission-enum.js";
import projectUtils from "../../../../utils/project-utils.js";
import UserService from "../../../../services/web/user-service.js";
import AttendanceService from "../../../../services/dispatch/attendance-service.js";
import AttendanceActionsCard from "./components/attendance-actions-card.jsx";
import AttendanceCallCard from "./components/attendance-call-card.jsx";
import AttendanceChatCard from "./components/attendance-chat-card.jsx";
import AttendanceCountModal from "./components/attendance-count-modal.jsx";
import AttendanceDataCard from "./components/attendance-data-card.jsx";
import AttendanceObservationsCard from "./components/attendance-observations-card.jsx";
import AttendanceRelatedTables from "./components/attendance-related-tables.jsx";
import {finishDashboardEdit} from "../../../../utils/dashboard-edit-window.js";

const BASENAME = import.meta.env.BASE_URL;
const API_DESPACHO = import.meta.env.VITE_API_DESPACHO;
const API_DESPACHO_WEB = import.meta.env.VITE_API_DESPACHO_WEB;

const initialForm = {
    systemUpdate: "",
    start: "",
    end: "",
    showEnd: false,
    attendanceLinked: null,
    protocol: "",
    userCreated: "",
    lastUser: "",
    nature: null,
    priority: null,
    statusName: null,
    channel: null,
    contactName: "",
    contactPhone: "",
    contactCpf: "",
    anonymous: false,
    transcription: "",
    audioTranscription: "",
    observation: "",
    observationFinal: "",
    janitorialId: "",
    linkedLocked: false,
    scheduleId: "",
    sosId: "",
    notificationId: "",
    individualAlarmId: "",
    ramalIdentify: "",
    callFile: null,
};

const initialAddress = {
    place: "",
    placeId: "",
    type_place: "",
    street: "",
    number: "",
    neighborhood: "",
    crossroads: "",
    reference_point: "",
    city: "",
    latitude: "",
    longitude: "",
    sectors: [],
};

const initialRequesterAlert = {
    alert: false,
    message: "Preencha o telefone ou CPF",
    data: null,
};

const initialAddressAlert = {
    alert: false,
    message: "Preencha as informações de endereço",
    data: null,
};

const AttendanceForm = () => {
    const navigate = navigateHook();
    const loaderData = useLoaderData();
    const hasPermission = useHasPermission();
    const {id} = useParams();
    const [searchParams] = useSearchParams();
    const redir = searchParams.get("redir") ?? "";
    const notificationId = searchParams.get("notificationId");
    const individualAlarmId = searchParams.get("individualAlarmId");
    const focusField = searchParams.get("focus");

    const [form, setForm] = useState(initialForm);
    const [factAddress, setFactAddress] = useState(initialAddress);
    const [contactAddress, setContactAddress] = useState(initialAddress);
    const [optionsData, setOptionsData] = useState({
        attendances: [],
        natures: [],
        priorities: [],
        status: [],
        channels: [],
        boName: loaderData?.bo_name ?? "BO",
        sizeMaxText: "20MB",
        allowCortexIndividual: false,
    });
    const [meta, setMeta] = useState({
        entityLatitude: "",
        entityLongitude: "",
        hasAttendanceLinked: false,
        hasDispatch: false,
        dispatch: null,
        taskList: [],
        dispatchesList: [],
    });
    const [attaches, setAttaches] = useState([]);
    const [attachesDescription, setAttachesDescription] = useState({});
    const [requesterAlert, setRequesterAlert] = useState(initialRequesterAlert);
    const [countModal, setCountModal] = useState({open: false, type: "requester", data: null});
    const [isSaving, setIsSaving] = useState(false);

    const canCreateJanitorial = hasPermission(PermissionEnum.ERP_JANITORIAL);
    const canSearchCortex = hasPermission(PermissionEnum.CORTEX_INDIVIDUAL) && !!optionsData.allowCortexIndividual;

    const normalizeOption = (item) => ({
        ...item,
        value: item?.value ?? item?.id ?? item?.name ?? item?.text,
        label: item?.label ?? item?.text ?? item?.alias ?? item?.name ?? item?.id ?? item?.value,
    });

    const normalizeNatureOption = (item) => ({
        ...normalizeOption({
            ...item,
            value: item?.name ?? item?.alias ?? item?.id ?? item?.value,
            label: item?.alias ?? item?.text ?? item?.name ?? item?.label,
        }),
        natureId: item?.id,
        janitorial: !!item?.janitorial,
        attendanceProcedure: !!item?.attendanceProcedure,
    });

    const normalizeNameOption = (item) => normalizeOption({
        ...item,
        value: item?.name ?? item?.id ?? item?.value,
        label: item?.name ?? item?.text ?? item?.label,
    });

    const optionValue = (option) => {
        if (option == null || option === "") return "";
        if (typeof option === "object") return option.value ?? "";
        return option;
    };

    const optionForValue = (options = [], value) => {
        if (value == null || value === "") return null;
        if (typeof value === "object" && value.value !== undefined) return value;

        return options.find(option => String(option.value) === String(value)) ?? {value, label: value};
    };

    const parseArrayValue = (value) => {
        if (!value) return [];
        if (Array.isArray(value)) return value;

        if (typeof value === "string") {
            try {
                const parsed = JSON.parse(value);
                if (Array.isArray(parsed)) return parsed;
            } catch {
                return value.includes("|") ? value.split("|").map(item => item.trim()) : [value];
            }
        }

        return [value];
    };

    const parseDateValue = (value) => {
        if (!value) return null;
        if (value instanceof Date && isValid(value)) return value;

        const candidates = [
            "dd/MM/yyyy HH:mm:ss",
            "dd/MM/yyyy HH:mm",
            "yyyy-MM-dd HH:mm:ss",
            "yyyy-MM-dd HH:mm",
            "yyyy-MM-dd'T'HH:mm:ss.SSSXXX",
            "yyyy-MM-dd'T'HH:mm:ssXXX",
        ];

        for (const pattern of candidates) {
            const parsed = parse(String(value), pattern, new Date());
            if (isValid(parsed)) return parsed;
        }

        const parsed = new Date(value);
        return isValid(parsed) ? parsed : null;
    };

    const formatDateTime = (value) => {
        const parsed = parseDateValue(value);
        return parsed ? format(parsed, "dd/MM/yyyy HH:mm") : value ?? "";
    };

    const digitsOnly = (value) => String(value ?? "").replace(/[^0-9]+/g, "");

    const normalizeLegacyAttachment = (item) => {
        const attachId = item?.id ?? item?.attachId ?? item?.reference;
        const extension = item?.extension ?? item?.type ?? String(item?.name ?? "").split(".").pop();

        return {
            ...item,
            attachId,
            extension,
            type: extension,
            legacy: true,
            legacyOriginal: item,
        };
    };

    const buildAttendanceOptions = (items = []) => items.map(item => ({
        ...item,
        value: item.id,
        label: item.text,
    }));

    const countMessage = (attendances) => {
        const total = Number(attendances?.attendanceTotal ?? 0);
        if (!total) return {alert: false, message: "Primeiro atendimento"};

        const label = total > 1 ? "atendimentos" : "atendimento";
        const latestAttendanceDate = (attendances?.attendanceList ?? []).reduce((latest, attendance) => {
            if (!attendance?.startDate) return latest;

            const startDate = new Date(attendance?.startDate);
            if (!isValid(startDate)) return latest;

            return !latest || startDate > latest ? startDate : latest;
        }, null);

        const elapsedMinutes = latestAttendanceDate
            ? Math.max(0, Math.floor((Date.now() - latestAttendanceDate.getTime()) / 60000))
            : null;
        const days = elapsedMinutes !== null ? Math.floor(elapsedMinutes / 1440) : 0;
        const hours = elapsedMinutes !== null ? Math.floor((elapsedMinutes % 1440) / 60) : 0;
        const minutes = elapsedMinutes !== null ? elapsedMinutes % 60 : 0;
        const elapsedTime = elapsedMinutes !== null
            ? [
                days > 0 && `${days} ${days === 1 ? "dia" : "dias"}`,
                hours > 0 && `${hours} ${hours === 1 ? "hora" : "horas"}`,
                minutes > 0 && `${minutes} ${minutes === 1 ? "minuto" : "minutos"}`,
            ].filter(Boolean).join(" ") || "menos de 1 minuto"
            : null;
        const last = elapsedTime
            ? `último há ${elapsedTime}`
            : "último em atendimento agendado";

        return {alert: true, message: `${total} ${label} - ${last}`};
    };

    const applyLinkedAttendance = (data) => {

        setFactAddress({
            place: data?.factPlace ?? "",
            placeId: "",
            type_place: data?.factType ?? "",
            street: data?.factStreet ?? "",
            number: data?.factNumber ?? "",
            neighborhood: data?.factNeighborhood ?? "",
            crossroads: data?.factCrossroads ?? "",
            reference_point: data?.factReferencePoint ?? "",
            city: data?.factCity,
            latitude: data?.factLatitude ?? "",
            longitude: data?.factLongitude ?? "",
            sectors: parseArrayValue(data?.sectors),
        });

        setForm(prev => ({
            ...prev,
            end: data ? formatDateTime(data.end) || format(new Date(), "dd/MM/yyyy HH:mm") : "",
            showEnd: !!data,
            nature: optionForValue(optionsData.natures, data?.nature ?? ""),
            priority: optionForValue(optionsData.priorities, data?.priority ?? ""),
            linkedLocked: !!data,
        }));
    };

    const applyPreviousRequester = (data) => {
        if (!data) return;

        setForm(prev => ({
            ...prev,
            contactPhone: data.contactPhone ? projectUtils.formatPhone(data.contactPhone) : prev.contactPhone,
            contactCpf: data.contactCpf ? projectUtils.formatCpfToView(data.contactCpf) : prev.contactCpf,
            contactName: data.contactName ?? prev.contactName,
        }));

        setContactAddress(prev => ({
            ...prev,
            place: data.contactPlace ?? prev.place,
            street: data.contactStreet ?? prev.street,
            crossroads: data.contactCrossroads ?? prev.crossroads,
            reference_point: data.contactReferencePoint ?? prev.reference_point,
            number: data.contactNumber ?? prev.number,
            neighborhood: data.contactNeighborhood ?? prev.neighborhood,
            city: data.contactCity ?? prev.city,
        }));
    };

    const resetForm = (nextOptions = optionsData) => {
        setForm({
            ...initialForm,
            start: format(new Date(), "dd/MM/yyyy HH:mm"),
            priority: nextOptions.priorities.find(item => item.priorityDefault) ?? null,
            statusName: nextOptions.status.find(item => item.statusDefault) ?? null,
            channel: nextOptions.channels.find(item => item.channelDefault) ?? null,
            notificationId: notificationId ?? "",
            individualAlarmId: individualAlarmId ?? "",
        });
        setFactAddress(initialAddress);
        setContactAddress(initialAddress);
        setAttaches([]);
        setAttachesDescription({});
        setRequesterAlert(initialRequesterAlert);
    };

    const applyExternalPrefill = async () => {
        if (id || (!notificationId && !individualAlarmId)) return null;

        const user = await UserService.getUserLogged().catch(() => null);
        const patch = {
            contactName: user?.name ?? "",
            contactPhone: projectUtils.formatPhone(user?.mainContact) ?? "",
            contactCpf: projectUtils.formatCpfToView(user?.cpf) ?? "",
        };

        if (notificationId) {
            const notification = await AttendanceService.getNotification(notificationId).catch(() => null);
            return {
                ...patch,
                notificationId,
                transcription: `${notification?.title ?? ""}\nAtendimento cadastrado via Notificação do Sentry.`.trim(),
            };
        }

        if (individualAlarmId) {
            await AttendanceService.getIndividualAlarm(individualAlarmId).catch(() => null);
            return {
                ...patch,
                individualAlarmId,
                transcription: "Atendimento cadastrado via Alarme de indivíduos.",
            };
        }

        return null;
    };

    const applyInitialData = useCallback(async () => {
        let rawData;
        let attendances;

        if (id) {
            rawData = await AttendanceService.get(id).catch(() => null);
            if (!rawData) {
                navigate("/404");
                return;
            }
            attendances = await AttendanceService.attendancesOptions(id);
        } else {
            [rawData, attendances] = await Promise.all([
                AttendanceService.get(),
                AttendanceService.attendancesOptions(),
            ]);
        }

        const attendance = rawData?.attendance ?? {};
        const natures = (rawData?.natures ?? []).map(normalizeNatureOption);
        const priorities = (rawData?.priorities ?? []).map(normalizeNameOption);
        const status = (rawData?.status ?? []).map(normalizeNameOption);
        const channels = (rawData?.channels ?? []).map(normalizeNameOption);

        const nextOptions = {
            attendances: buildAttendanceOptions(attendances),
            natures,
            priorities,
            status,
            channels,
            boName: rawData?.boName ?? loaderData?.bo_name ?? "BO",
            sizeMaxText: rawData?.sizeMaxText ?? "20MB",
            allowCortexIndividual: !!rawData?.allowCortexIndividual,
        };

        setOptionsData(nextOptions);

        const externalPrefill = await applyExternalPrefill();
        const priorityDefault = attendance.priority ?? rawData?.priorityDefault ?? priorities.find(item => item.priorityDefault)?.value ?? "";
        const statusDefault = attendance.statusName ?? rawData?.statusDefault ?? status.find(item => item.statusDefault)?.value ?? "";
        const channelDefault = attendance.channel ?? rawData?.channelDefault ?? channels.find(item => item.channelDefault)?.value ?? "";
        const linkedOption = attendance.attendanceLinked?.id ? optionForValue(nextOptions.attendances, attendance.attendanceLinked.id) : null;

        const nextForm = {
            ...initialForm,
            start: formatDateTime(attendance.start) || format(new Date(), "dd/MM/yyyy HH:mm"),
            end: formatDateTime(attendance.end),
            showEnd: !!attendance.end,
            attendanceLinked: linkedOption,
            protocol: attendance.protocol ?? "",
            userCreated: rawData?.userCreated ?? "",
            lastUser: attendance.lastUser ?? "",
            nature: optionForValue(natures, attendance.nature),
            priority: optionForValue(priorities, priorityDefault),
            statusName: optionForValue(status, statusDefault),
            channel: optionForValue(channels, channelDefault),
            contactName: attendance.contactName ?? "",
            contactPhone: projectUtils.formatPhone(attendance.contactPhone) ?? "",
            contactCpf: projectUtils.formatCpfToView(attendance.contactCpf) ?? "",
            anonymous: !!attendance.anonymous,
            transcription: attendance.transcription ?? "",
            audioTranscription: attendance.audioTranscription ?? "",
            observation: attendance.observation ?? "",
            observationFinal: attendance.observationFinal ?? "",
            systemUpdate: attendance.systemUpdate ?? "",
            janitorialId: attendance.janitorialId ?? "",
            linkedLocked: !!linkedOption,
            scheduleId: attendance.scheduleId ?? "",
            sosId: attendance.sosId ?? "",
            notificationId: attendance.notificationId ?? notificationId ?? "",
            individualAlarmId: attendance.individualAlarmId ?? individualAlarmId ?? "",
            ramalIdentify: attendance.ramalIdentify ?? "",
            callFile: attendance.callFile ?? null,
            ...externalPrefill,
        };

        setForm(nextForm);
        setFactAddress({
            place: attendance.factPlace ?? "",
            placeId: "",
            type_place: attendance.factType ?? "",
            street: attendance.factStreet ?? "",
            number: attendance.factNumber ?? "",
            neighborhood: attendance.factNeighborhood ?? "",
            crossroads: attendance.factCrossroads ?? "",
            reference_point: attendance.factReferencePoint ?? "",
            city: attendance.factCity ?? rawData?.entityCity ?? "",
            latitude: attendance.factLatitude ?? "",
            longitude: attendance.factLongitude ?? "",
            sectors: parseArrayValue(attendance.sectors),
        });
        setContactAddress({
            place: attendance.contactPlace ?? "",
            placeId: "",
            type_place: attendance.contactType ?? "",
            street: attendance.contactStreet ?? "",
            number: attendance.contactNumber ?? "",
            neighborhood: attendance.contactNeighborhood ?? "",
            crossroads: attendance.contactCrossroads ?? "",
            reference_point: attendance.contactReferencePoint ?? "",
            city: attendance.contactCity,
            latitude: "",
            longitude: "",
            sectors: [],
        });

        const dispatchesList = [...(rawData?.dispatchesList ?? [])].sort((a, b) => b.id - a.id);
        const dispatch = dispatchesList[0] ?? null;

        setMeta({
            entityLatitude: rawData?.entityLatitude ?? "",
            entityLongitude: rawData?.entityLongitude ?? "",
            hasAttendanceLinked: !!rawData?.hasAttendanceLinked,
            hasDispatch: !!dispatch,
            dispatch,
            taskList: rawData?.taskList ?? [],
            dispatchesList,
        });

        const attachList = attendance.attachment ?? [];
        const descriptions = attachList.reduce((acc, attach) => {
            const attachId = attach?.id ?? attach?.attachId ?? attach?.reference;
            if (attachId) acc[attachId] = attach?.description ?? "";
            return acc;
        }, {});

        setAttachesDescription(descriptions);
        setAttaches(attachList.map(normalizeLegacyAttachment));

        if (id) {
            refreshRequesterCount(nextForm);
        }

        if (!id && externalPrefill) {
            setForm(prev => ({...prev, ...externalPrefill}));
        }

        return rawData;
    }, [id, individualAlarmId, loaderData?.bo_name, notificationId]);

    const {isLoading: isQueryLoading} = useQuery({
        queryFn: applyInitialData,
        queryKey: ["dispatch-attendance-form", id ?? "new", notificationId, individualAlarmId],
    });

    useEffect(() => {
        if (!focusField || isQueryLoading) return;

        const element = document.getElementById(focusField);
        if (!element) return;

        element.focus({preventScroll: true});
        element.scrollIntoView({behavior: "smooth", block: "center"});
    }, [focusField, isQueryLoading]);

    const isNatureJanitorial = !!form.nature?.janitorial;

    const setFormField = (field, value, options = {}) => {
        setForm(prev => ({...prev, [field]: value}));

        if (options.refreshRequester) {
            const nextForm = {...form, [field]: value};
            refreshRequesterCount(nextForm, field === "contactPhone");
        }
    };

    async function refreshRequesterCount(source = form, prefillByPhone = false) {
        const phone = digitsOnly(source.contactPhone);
        const cpf = digitsOnly(source.contactCpf);

        if (!phone && !cpf) {
            setRequesterAlert(initialRequesterAlert);
            return;
        }

        try {
            const data = await AttendanceService.attendanceTableListByRequester({
                attendanceId: id ?? null,
                phone,
                cpf,
            });

            const info = countMessage(data?.attendances);
            setRequesterAlert({...info, data});

            if (prefillByPhone && phone && !id) {
                const previous = await AttendanceService.previousByPhone(phone).catch(() => null);
                applyPreviousRequester(previous);
            }
        } catch (error) {
            console.error(error);
            setRequesterAlert(initialRequesterAlert);
        }
    }

    const hasAddressForCount = (address = factAddress) => {
        return address.place || (address.city && address.neighborhood && address.street && address.number);
    };

    const addressCountEnabled = !!hasAddressForCount();

    const {data: addressCountData} = useQuery({
        queryKey: [
            "dispatch-attendance-address-count",
            id ?? "new",
            factAddress.place,
            factAddress.city,
            factAddress.neighborhood,
            factAddress.street,
            factAddress.number,
        ],
        queryFn: () => AttendanceService.attendanceTableListByAddress({
            attendanceId: id ?? null,
            factCity: factAddress.city,
            factPlace: factAddress.place,
            factNeighborhood: factAddress.neighborhood,
            factStreet: factAddress.street,
            factNumber: factAddress.number,
        }),
        enabled: addressCountEnabled,
        placeholderData: keepPreviousData,
    });

    const addressAlert = useMemo(() => (
        addressCountEnabled && addressCountData ? {...countMessage(addressCountData.attendances), data: addressCountData} : initialAddressAlert
    ), [addressCountEnabled, addressCountData]);

    const openAddressCount = () => {
        if (!addressAlert.alert) return;
        setCountModal({open: true, type: "address", data: addressAlert.data});
    };

    const handleLinkedAttendance = (option) => {
        setForm(prev => ({
            ...prev,
            attendanceLinked: option,
            statusName: option ? {value: "VINCULADO", label: "VINCULADO", closedAttendance: true} : null,
            linkedLocked: !!option,
        }));

        if (!option) {
            applyLinkedAttendance(null);
            return;
        }

        AttendanceService.attendanceSelected(option.value)
            .then(applyLinkedAttendance)
            .catch((error) => {
                console.error(error);
                projectUtils.swalErrorMessage("", "Não foi possível carregar o atendimento vinculado.", "Ok");
            });
    };

    const openJanitorial = (janitorialId) => window.open(`${BASENAME}janitorial/form/${janitorialId}`, "_blank");

    const copyFactToContact = () => {
        setContactAddress(prev => ({
            ...prev,
            place: factAddress.place,
            placeId: factAddress.placeId,
            type_place: factAddress.type_place,
            street: factAddress.street,
            number: factAddress.number,
            neighborhood: factAddress.neighborhood,
            crossroads: factAddress.crossroads,
            reference_point: factAddress.reference_point,
            city: factAddress.city,
        }));
    };

    const buildFormData = (createJanitorialOnSave = false) => {
        const formData = new FormData();
        const attachmentDescriptionNew = [];

        attaches.forEach((attach) => {
            if (!(attach instanceof File) || attach.legacy) return;

            const description = attachesDescription[attach.attachId];
            if (description) attachmentDescriptionNew.push({name: attach.name, description});
            formData.append("attachFiles", attach);
        });

        const statusClosed = form.statusName?.closedAttendance !== false && String(form.statusName?.closedAttendance) !== "false";
        const attachment = attaches
            .filter(attach => attach.legacy)
            .map(attach => ({
                ...attach.legacyOriginal,
                description: attachesDescription[attach.attachId] ?? attach.legacyOriginal?.description ?? null,
            }));

        const model = {
            createJanitorial: createJanitorialOnSave,
            systemUpdate: form.systemUpdate,
            start: form.start,
            end: form.end ? (statusClosed ? form.end : undefined) : undefined,
            attendanceLinked: form.attendanceLinked ? {id: optionValue(form.attendanceLinked)} : {},
            contactName: form.contactName,
            contactPhone: digitsOnly(form.contactPhone),
            contactCpf: digitsOnly(form.contactCpf),
            lastUser: form.lastUser,
            nature: optionValue(form.nature),
            channel: optionValue(form.channel),
            priority: optionValue(form.priority),
            statusName: optionValue(form.statusName),
            status: !statusClosed,
            anonymous: !!form.anonymous,
            transcription: form.transcription,
            audioTranscription: form.audioTranscription,
            factCity: factAddress.city,
            factType: factAddress.type_place,
            factPlace: factAddress.place,
            factNeighborhood: factAddress.neighborhood,
            factStreet: factAddress.street,
            factCrossroads: factAddress.crossroads,
            factReferencePoint: factAddress.reference_point,
            factLatitude: factAddress.latitude,
            factLongitude: factAddress.longitude,
            factNumber: factAddress.number,
            sectors: parseArrayValue(factAddress.sectors).map(optionValue),
            contactCity: contactAddress.city,
            contactType: contactAddress.type_place,
            contactPlace: contactAddress.place,
            contactNeighborhood: contactAddress.neighborhood,
            contactStreet: contactAddress.street,
            contactCrossroads: contactAddress.crossroads,
            contactReferencePoint: contactAddress.reference_point,
            contactNumber: contactAddress.number,
            observation: form.observation,
            observationFinal: form.observationFinal,
            attachment,
            attachmentDescription: attachmentDescriptionNew.length ? attachmentDescriptionNew : null,
        };

        if (id) model.id = id;
        else if (notificationId) model.notificationId = notificationId;
        else if (individualAlarmId) model.individualAlarmId = individualAlarmId;

        const normalizedModel = Object.fromEntries(
            Object.entries(model).map(([key, value]) => [key, value === "" ? null : value])
        );

        formData.append("myModel", JSON.stringify(normalizedModel));
        return formData;
    };

    const validate = () => {
        if (!form.start) return "O campo \"Data/Hora inicial\" é obrigatório.";
        if (!optionValue(form.nature)) return "O campo \"Natureza\" é obrigatório.";
        if (!optionValue(form.statusName)) return "O campo \"Status\" é obrigatório.";
        if (!optionValue(form.channel)) return "O campo \"Canal\" é obrigatório.";
        if (!form.transcription) return "O campo \"Transcrição\" é obrigatório.";

        if (factAddress.latitude === null || factAddress.latitude === undefined || String(factAddress.latitude).trim() === "") {
            return "O campo \"Latitude\" é obrigatório.";
        }

        if (factAddress.longitude === null || factAddress.longitude === undefined || String(factAddress.longitude).trim() === "") {
            return "O campo \"Longitude\" é obrigatório.";
        }

        const lat = Number(factAddress.latitude);
        const lng = Number(factAddress.longitude);
        if (!Number.isFinite(lat) || lat < -90 || lat > 90 || !Number.isFinite(lng) || lng < -180 || lng > 180) {
            return "Latitude ou longitude inválida.";
        }

        return "";
    };

    const save = (addAfter = false, createJanitorialOnSave = false) => {
        const message = validate();
        if (message) {
            projectUtils.swalErrorMessage("", message, "Ok");
            return;
        }

        if (id) {
            const warnings = [];
            if (meta.hasAttendanceLinked) warnings.push("- Atendimentos vinculados: natureza, prioridade, setor e endereço do fato;");
            if (meta.hasDispatch) warnings.push("- Despachos vinculados: setor e endereço do fato;");

            if (warnings.length) {
                projectUtils.swalMessage(
                    "warning",
                    "As seguintes informações serão alteradas:",
                    warnings.join("\n"),
                    "Sim",
                    true,
                    "Não",
                    ({isConfirmed}) => { if (isConfirmed) submit(addAfter, createJanitorialOnSave); },
                );
                return;
            }

            submit(addAfter, createJanitorialOnSave);
            return;
        }

        if (!form.janitorialId && isNatureJanitorial && canCreateJanitorial) {
            projectUtils.swalMessage(
                "warning",
                "",
                "Deseja criar zeladoria para esse atendimento?",
                "Sim",
                true,
                "Não",
                ({isConfirmed}) => submit(addAfter, !!isConfirmed),
            );
            return;
        }

        submit(addAfter, createJanitorialOnSave);
    };

    const submit = (addAfter = false, createJanitorialOnSave = false) => {
        setIsSaving(true);
        const request = id ? AttendanceService.update(id, buildFormData(createJanitorialOnSave)) : AttendanceService.save(buildFormData(createJanitorialOnSave));

        request
            .then((data) => {
                projectUtils.success(true);

                const janitorialId = data?.data?.attendance?.janitorialId ?? data?.attendance?.janitorialId;
                if (createJanitorialOnSave && janitorialId) openJanitorial(janitorialId);

                if (addAfter) {
                    resetForm();
                    navigate(`${API_DESPACHO_WEB}/attendance/form${redir ? `?redir=${redir}` : ""}`, false);
                    return;
                }

                if (redir) {
                    if (id && finishDashboardEdit(redir, navigate, "Atendimento salvo com sucesso.")) return;
                    navigate(`/${redir}`, false);
                    return;
                }

                navigate(API_DESPACHO_WEB + "/attendance", false);
            })
            .catch((error) => {
                console.error(error);
                const message = error.response?.data?.errorMessages ?? "Não foi possível salvar.";
                projectUtils.swalErrorMessage("", message, "Ok");
            })
            .finally(() => setIsSaving(false));
    };

    const scheduleLink = form.scheduleId ? {
        dev: `${API_DESPACHO_WEB}/schedule-attendance/form/${form.scheduleId}`,
    } : null;

    const callFileUrl = form.callFile?.id ? AttendanceService.attachmentUrl(form.callFile.id) : "";

    const isLoading = isQueryLoading || isSaving;

    return (
        <Template isLoading={isLoading}>
            <Template.Header>
                <Template.Title browserTitle={'Atendimento'}>
                    Atendimento {id ? <span className="text-[12px]">(ID: {id})</span> : null}
                    {scheduleLink && (
                        <button
                            type="button"
                            className="ml-1 cursor-pointer text-blue-700 text-[12px]"
                            onClick={() => navigate(scheduleLink.dev, true)}
                        >
                            (Cadastrado por agendamento <FaExternalLinkAlt className="inline"/>)
                        </button>
                    )}
                </Template.Title>
                {id && (
                    <div className="flex gap-2">
                        <Button title="Imprimir" onClick={() => window.open(`${BASENAME}reports/attendance_info?id=${id}`, "_blank")}><FaPrint/></Button>
                        <Button target="primary-outline" onClick={() => navigate(API_DESPACHO_WEB + "/attendance/form", false)}>
                            <FaPlus/>Adicionar atendimento
                        </Button>
                    </div>
                )}
            </Template.Header>
            <Template.Body className="flex flex-col space-y-2">
                <AttendanceDataCard
                    id={id}
                    form={form}
                    optionsData={optionsData}
                    dispatch={meta.dispatch}
                    requesterAlert={requesterAlert}
                    canCreateJanitorial={canCreateJanitorial}
                    canSearchCortex={canSearchCortex}
                    onChangeField={setFormField}
                    onChangeLinkedAttendance={handleLinkedAttendance}
                    onChangeContactAddress={setContactAddress}
                    onOpenRequesterModal={() => setCountModal({open: true, type: "requester", data: requesterAlert.data})}
                    setIsLoading={setIsSaving}
                />

                <AttendanceCallCard
                    ramalIdentify={form.ramalIdentify}
                    callFileUrl={callFileUrl}
                    setIsLoading={setIsSaving}
                    onChangeField={setFormField}
                    form={form}
                />

                <RegisteredLocation
                    title="Endereço do fato"
                    data={factAddress}
                    setData={setFactAddress}
                    showSectors
                    storePlaceName
                    coordinatesRequired={false}
                    disabled={form.linkedLocked}
                    actionInfo={
                        <Button
                            className="h-7 px-2"
                            target={addressAlert.alert ? "danger" : "primary"}
                            title={addressAlert.message}
                            disabled={!addressAlert.alert}
                            onClick={openAddressCount}
                        >
                            {addressAlert.alert ? <FaExclamationTriangle/> : <FaInfoCircle/>}
                        </Button>
                    }
                    actionCopy={
                        <Button className="h-7 px-2" target="primary-outline" title="Copiar para o endereço do solicitante" onClick={copyFactToContact}>
                            <FaArrowDown/> Copiar
                        </Button>
                    }
                />

                <RegisteredLocation
                    title="Endereço do solicitante"
                    data={contactAddress}
                    setData={setContactAddress}
                    storePlaceName
                    showCoordinates={false}
                    showMap={false}
                />

                <AttendanceObservationsCard form={form} onChangeField={setFormField}/>

                <Dropzone
                    title="Anexos"
                    attaches={attaches}
                    setAttaches={setAttaches}
                    attachesDescription={attachesDescription}
                    setAttachesDescription={setAttachesDescription}
                    attachUrl={API_DESPACHO + "/attachment/get/"}
                    pdfUrl={API_DESPACHO + "/attachment/print/"}
                />

                <AttendanceChatCard id={id} show={!!form.end}/>

                <AttendanceRelatedTables
                    taskList={meta.taskList}
                    dispatchesList={meta.dispatchesList}
                    boName={optionsData.boName}
                    dispatchUrl={(dispatchId) => ({
                        dev: `${API_DESPACHO_WEB}/dispatch/form/${dispatchId}`,
                    })}
                    occurrenceUrl={(occurrenceNumber) => {
                        const occurrenceId = String(occurrenceNumber).replaceAll("/", "-");
                        return {dev: `/bo/redirect/${occurrenceId}`};
                    }}
                />

                <AttendanceActionsCard
                    isSaving={isSaving}
                    onSaveAndAdd={() => save(true)}
                    onSave={() => save(false)}
                />
            </Template.Body>

            <AttendanceCountModal
                open={countModal.open}
                setOpen={(open) => setCountModal(prev => ({...prev, open}))}
                type={countModal.type}
                data={countModal.data}
            />
        </Template>
    );
};

export default AttendanceForm;